import { useState } from "react";
import { Loader2, Sparkles, CheckCircle2, AlertTriangle, BookCheck, ListPlus, FileText } from "lucide-react";
import { parseJournal, bulkImport } from "@/lib/api";
import { toast } from "sonner";

const EXAMPLE = `Lord Jacob Rothschild dies 6 years 6 months 6 days after eclipse.
Jacob Rothschild =66, Lord Jacob Rothschild =317 (66th prime).
"crossbow killer" =64, murder =83, kill =44.
Hulk Hogan =38, summer solstice =88, Trump =88.`;

const EXAMPLE_LIST = `Operation Midnight Hammer
Ides Of March
Hantavirus Outbreak
Bondi Beach Shooting
Crowbar
Chief Rabbi Ephraim Mirvis`;

export default function Journal({ onChange }) {
  const [mode, setMode] = useState("decode"); // 'decode' | 'list'
  return (
    <section className="space-y-10">
      <div className="rise">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] muted mb-3">
          // Decode Journal &amp; Import
        </div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight">
          Paste your notes. <span className="gold italic">Auto-verify the math.</span>
        </h1>
        <p className="muted mt-4 max-w-2xl leading-relaxed">
          Drop in raw decoding notes — we&apos;ll extract every <span className="font-mono gold">phrase = N</span> claim,
          re-run the cipher math, and let you add the new ones to the dictionary in one click.
          Or just paste a list of phrases to bulk-import.
        </p>
      </div>

      <div className="rise rise-1 flex gap-2">
        <ModeBtn active={mode === "decode"} onClick={() => setMode("decode")} testid="journal-mode-decode" icon={<BookCheck size={14} />}>
          Decode Journal
        </ModeBtn>
        <ModeBtn active={mode === "list"} onClick={() => setMode("list")} testid="journal-mode-list" icon={<ListPlus size={14} />}>
          Bulk List Import
        </ModeBtn>
      </div>

      {mode === "decode" ? <DecodePane onChange={onChange} example={EXAMPLE} /> : <BulkPane onChange={onChange} example={EXAMPLE_LIST} />}
    </section>
  );
}

function ModeBtn({ active, onClick, children, testid, icon }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2.5 text-xs font-mono uppercase tracking-[0.14em] border flex items-center gap-2 transition-colors ${
        active
          ? "border-[var(--primary)] text-[var(--primary)] bg-[rgba(212,175,55,0.08)]"
          : "border-white/10 muted hover:text-white"
      }`}
    >
      {icon} {children}
    </button>
  );
}

/* ------------------- DECODE PANE ------------------- */
function DecodePane({ onChange, example }) {
  const [text, setText] = useState("");
  const [parsing, setParsing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [claims, setClaims] = useState(null);
  const [defaultTags, setDefaultTags] = useState("");
  const [selected, setSelected] = useState([]);

  const run = async () => {
    if (!text.trim()) return;
    setParsing(true);
    try {
      const data = await parseJournal(text);
      setClaims(data.claims);
      // Auto-select all verified, not-yet-existing entries.
      const nextSelected = data.claims
        .map((c, i) => (c.verified && !c.exists ? i : -1))
        .filter((i) => i >= 0);
      setSelected(nextSelected);
      if (data.total === 0) toast("No `phrase = number` claims detected.");
      else toast.success(`Found ${data.total} decode claim${data.total === 1 ? "" : "s"}`);
    } catch {
      toast.error("Failed to parse — try again.");
    } finally {
      setParsing(false);
    }
  };

  const toggle = (i) =>
    setSelected((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]));

  const addSelected = async () => {
    if (!claims || selected.length === 0) return;
    setAdding(true);
    const tagList = defaultTags.split(",").map((t) => t.trim()).filter(Boolean);
    const payload = selected.map((i) => {
      const c = claims[i];
      const matched = c.matching_ciphers.join(", ") || "no exact cipher match";
      return {
        text: c.phrase,
        tags: tagList,
        notes: `Decodes to ${c.claimed_value} (${matched})`,
      };
    });
    try {
      const data = await bulkImport(payload);
      toast.success(`Added ${data.inserted}, skipped ${data.skipped}`);
      onChange?.();
      // Re-parse to refresh "exists" markers.
      const data2 = await parseJournal(text);
      setClaims(data2.claims);
      setSelected([]);
    } catch {
      toast.error("Bulk add failed");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="rise rise-2 space-y-6">
      <div className="panel p-6 md:p-8">
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] muted">
          Paste decoding notes
        </label>
        <textarea
          data-testid="journal-text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          placeholder={example}
          className="terminal-input mt-2 w-full px-4 py-3 text-sm resize-y font-mono"
        />

        <div className="mt-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="font-mono text-[10px] uppercase tracking-wider muted">
              Default tags applied to all added phrases
            </label>
            <input
              data-testid="journal-default-tags"
              value={defaultTags}
              onChange={(e) => setDefaultTags(e.target.value)}
              placeholder="my-decodes, event"
              className="terminal-input mt-1.5 w-full px-3 py-2 text-sm"
            />
          </div>
          <button
            data-testid="journal-parse-btn"
            onClick={run}
            disabled={parsing || !text.trim()}
            className="btn-primary px-5 py-3 text-xs flex items-center gap-2"
          >
            {parsing ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
            Parse Claims
          </button>
        </div>
      </div>

      {claims !== null && (
        <ClaimsResults
          claims={claims}
          selected={selected}
          adding={adding}
          onToggle={toggle}
          onAddSelected={addSelected}
        />
      )}
    </div>
  );
}

function ClaimsResults({ claims, selected, adding, onToggle, onAddSelected }) {
  const verifiedCount = claims.filter((c) => c.verified).length;
  const newCount = claims.filter((c) => c.verified && !c.exists).length;

  return (
    <div className="rise space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] muted">Extracted</div>
          <div className="font-display text-3xl">
            <span className="gold">{claims.length}</span> claim{claims.length === 1 ? "" : "s"}{" "}
            <span className="muted text-base">• {verifiedCount} verified • {newCount} new</span>
          </div>
        </div>
        <button
          data-testid="journal-add-selected"
          onClick={onAddSelected}
          disabled={adding || selected.length === 0}
          className="btn-primary px-5 py-3 text-xs flex items-center gap-2 disabled:opacity-40"
        >
          {adding ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
          Add {selected.length || ""} Selected
        </button>
      </div>

      <div className="space-y-2" data-testid="journal-claims-list">
        {claims.length === 0 && (
          <div className="panel p-8 text-center muted font-mono text-sm">
            No <span className="gold">phrase = number</span> claims found. Try adding equals signs to your notes.
          </div>
        )}
        {claims.map((c, i) => (
          <ClaimRow
            key={`${c.phrase}::${c.claimed_value}`}
            claim={c}
            selected={selected.includes(i)}
            onToggle={() => onToggle(i)}
            idx={i}
          />
        ))}
      </div>
    </div>
  );
}

function getClaimStatus(claim) {
  if (claim.exists) {
    return { label: "Already in dictionary", color: "text-[var(--primary)]", icon: <BookCheck size={13} /> };
  }
  if (claim.verified) {
    const n = claim.matching_ciphers.length;
    return { label: `Verified in ${n} cipher${n === 1 ? "" : "s"}`, color: "text-[var(--primary)]", icon: <CheckCircle2 size={13} /> };
  }
  return { label: "No cipher matches that value", color: "text-[#6EE7B7]", icon: <AlertTriangle size={13} /> };
}

function ClaimRow({ claim, selected, onToggle, idx }) {
  const status = getClaimStatus(claim);

  return (
    <label
      data-testid={`journal-claim-${idx}`}
      className={`panel p-4 flex items-center gap-4 cursor-pointer transition-colors ${
        selected ? "border-[var(--primary)]" : ""
      } ${claim.exists ? "opacity-60" : ""}`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        disabled={claim.exists}
        className="w-4 h-4 accent-[var(--primary)]"
        data-testid={`journal-claim-check-${idx}`}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3 flex-wrap">
          <span className="font-display text-xl">{claim.phrase}</span>
          <span className="font-mono text-xs muted">=</span>
          <span className="font-mono big-number text-2xl">{claim.claimed_value}</span>
        </div>
        <div className={`mt-1.5 font-mono text-[10px] uppercase tracking-wider flex items-center gap-1.5 ${status.color}`}>
          {status.icon} {status.label}
        </div>
        {claim.matching_ciphers.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {claim.matching_ciphers.map((c) => (
              <span key={c} className="tag tag-warm">{c.replace(/_/g, " ")}</span>
            ))}
          </div>
        )}
      </div>
    </label>
  );
}

/* ------------------- BULK PANE ------------------- */
function BulkPane({ onChange, example }) {
  const [text, setText] = useState("");
  const [defaultTags, setDefaultTags] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!text.trim()) return;
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l && l.length >= 2);
    if (lines.length === 0) return;

    // Each line: phrase | tags | notes  (pipes optional)
    const phrases = lines.map((line) => {
      const parts = line.split("|").map((p) => p.trim());
      const phraseText = parts[0];
      const tags = parts[1] ? parts[1].split(",").map((t) => t.trim()).filter(Boolean) : [];
      const notes = parts[2] || "";
      return { text: phraseText, tags, notes };
    });

    const defaults = defaultTags.split(",").map((t) => t.trim()).filter(Boolean);

    setImporting(true);
    try {
      const data = await bulkImport(phrases, defaults);
      setResult(data);
      toast.success(`Imported ${data.inserted}, skipped ${data.skipped}`);
      onChange?.();
    } catch {
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="rise rise-2 space-y-6">
      <div className="panel p-6 md:p-8">
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] muted">
          One phrase per line. Optional: <span className="gold">phrase | tag1, tag2 | notes</span>
        </label>
        <textarea
          data-testid="bulk-text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={10}
          placeholder={example}
          className="terminal-input mt-2 w-full px-4 py-3 text-sm resize-y font-mono"
        />
        <div className="mt-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[220px]">
            <label className="font-mono text-[10px] uppercase tracking-wider muted">
              Default tags applied to every line
            </label>
            <input
              data-testid="bulk-default-tags"
              value={defaultTags}
              onChange={(e) => setDefaultTags(e.target.value)}
              placeholder="my-decodes"
              className="terminal-input mt-1.5 w-full px-3 py-2 text-sm"
            />
          </div>
          <button
            data-testid="bulk-import-btn"
            onClick={run}
            disabled={importing || !text.trim()}
            className="btn-primary px-5 py-3 text-xs flex items-center gap-2"
          >
            {importing ? <Loader2 className="animate-spin" size={14} /> : <FileText size={14} />}
            Import
          </button>
        </div>
      </div>

      {result && (
        <div className="panel p-6 rise" data-testid="bulk-result">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] muted">Result</div>
          <div className="grid grid-cols-3 gap-4 mt-3 font-mono">
            <Box label="Added" value={result.inserted} color="gold" />
            <Box label="Skipped (dup)" value={result.skipped} />
            <Box label="Errors" value={result.errors.length} />
          </div>
          {result.errors.length > 0 && (
            <ul className="mt-3 text-xs text-[#6EE7B7] font-mono space-y-1">
              {result.errors.slice(0, 10).map((e) => (
                <li key={e}>• {e}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function Box({ label, value, color }) {
  return (
    <div className="border border-white/10 p-4 bg-black/30 text-center">
      <div className="font-mono text-[10px] uppercase tracking-wider muted">{label}</div>
      <div className={`text-3xl mt-1 ${color === "gold" ? "big-number" : ""}`}>{value}</div>
    </div>
  );
}
